import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import CardLayout from '../../../shared/components/CardLayout';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { OfferService } from '../OfferService';
import { formatDate, getCorrectEnums, getPlatformValue } from '../../../utils/Functions';
import { ErrorToast } from '../../../utils/toasters';
import { OfferItemEvent } from '../../../model/offer/OfferItemEvent';

export function OfferEvents() {
    const { offerId } = useParams<{ offerId: string }>();
    const navigate = useNavigate();
    const [events, setEvents] = useState<OfferItemEvent[]>([]);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        if (!offerId) return;

        setDataLoading(true);
        OfferService.getOfferEvents(Number(offerId))
            .then(data => setEvents(data))
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [offerId]);

    const renderStatusIcon = (condition?: boolean) =>
        condition ? (
            <span className="word-break text-primary">
                <FaCheck />
            </span>
        ) : (
            <span className="word-break text-danger">
                <FaTimes />
            </span>
        );

    return (
        <CardLayout classCard="mb-5" classBody="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Events for Offer #{offerId}</h5>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/offers')}>
                    Offers
                </Button>
            </div>

            {dataLoading && <div className="text-muted mb-3">Loading events...</div>}

            {!dataLoading && events.length === 0 && (
                <div className="text-muted">No events found for this offer.</div>
            )}

            {!dataLoading && events.length > 0 && (
                <Table hover responsive>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Influencer</th>
                            <th>Platform</th>
                            <th>Posting Date</th>
                            <th>Status</th>
                            <th>Approved</th>
                            <th>Approved By</th>
                            <th>Invoiced</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map(event => (
                            <tr key={event.id}>
                                <td>{event.id}</td>
                                <td>
                                    <ImpersonateEye id={event.user?.id ?? 0} />
                                    {event.user?.displayName}
                                </td>
                                <td>
                                    {event.offerItem?.socialPlatform ? (
                                        <div className="d-flex align-items-center my-2">
                                            <img
                                                src={event.offerItem.socialPlatform.path}
                                                alt={event.offerItem.socialPlatform.name}
                                                width={30}
                                            />
                                            <div className="d-flex flex-column">
                                                <span className="font-weight-bolder ml-1">
                                                    {getPlatformValue(event.offerItem.socialPlatform.name ?? '')}
                                                </span>
                                                <span className="ml-1">
                                                    {event.platformPosition ?? '-'}/{event.offerItem?.nbPosts ?? '-'}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td>{formatDate(event.postingDate)}</td>
                                <td>
                                    <span className={`status ${event.status}`}>{getCorrectEnums(event.status)}</span>
                                </td>
                                <td>{renderStatusIcon(!!event.approvedBy)}</td>
                                <td>{event.approvedBy?.displayName ?? '-'}</td>
                                <td>{renderStatusIcon(event.isInvoiced)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </CardLayout>
    );
}
